import { CSSProperties, useEffect, useId, useRef, useState } from "react";
import { clock } from "../api";
import { country, date, language, route } from "./api";
import { useApp } from "./context";
import { usePlayer } from "./player";
import { Artwork, Icon } from "./visual";
import { currentWeek, wheelShows, wheelDelta, wheelSlots } from "./week";

function WheelPlaybackGlyph({ playing }: { playing: boolean }) {
  const gradient = useId();
  return (
    <svg
      className={"dial-play-glyph" + (playing ? " is-playing" : "")}
      viewBox="0 0 100 100"
      aria-hidden="true"
      fill={`url(#${gradient})`}
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="0.8" y2="1">
          <stop stopColor="currentColor" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      <path
        className="dial-play-symbol"
        d="M34 19C30 16.6 25 19.5 25 24V76C25 80.5 30 83.4 34 81L78 55C82 52.6 82 47.4 78 45Z"
      />
      <g className="dial-pause-symbol">
        <rect x="33" y="24" width="12" height="52" rx="5" />
        <rect x="55" y="24" width="12" height="52" rx="5" />
      </g>
    </svg>
  );
}

export default function WeeklyIntro() {
  const { catalogue, t, lang, title, appearance } = useApp();
  const player = usePlayer();
  const [today, setToday] = useState(() => new Date());
  const [turn, setTurn] = useState(0);
  const [automatic, setAutomatic] = useState(true);
  const [hover, setHover] = useState(false);
  const [visible, setVisible] = useState(false);
  const [foreground, setForeground] = useState(!document.hidden);
  const [deviceReduced, setDeviceReduced] = useState(
    () => matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const root = useRef<HTMLElement>(null);
  const episodes = wheelShows(catalogue.episodes, today);
  const count = episodes.length;
  const selected = count ? ((turn % count) + count) % count : 0;
  const episode = episodes[selected];
  const week = currentWeek(today);
  const reduced = deviceReduced || appearance.motion === "reduced";
  const step = 48;
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  const focusSelected = useRef<"dial" | "rail" | null>(null);
  const rail = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusSelected.current) {
      root.current
        ?.querySelector<HTMLButtonElement>(
          focusSelected.current === "rail"
            ? '.rail-choice[aria-pressed="true"]'
            : '.dial-choice[aria-current="true"]',
        )
        ?.focus({ preventScroll: true });
      focusSelected.current = null;
    }
  }, [turn]);
  useEffect(() => {
    const strip = rail.current;
    const choice = strip?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!strip || !choice) return;
    const bounds = strip.getBoundingClientRect(),
      item = choice.getBoundingClientRect();
    strip.scrollTo({
      left:
        strip.scrollLeft +
        item.left -
        bounds.left -
        (bounds.width - item.width) / 2,
      behavior: reduced ? "instant" : "smooth",
    });
  }, [selected, reduced]);
  const inWeek = Boolean(
    episode?.broadcast_date &&
    episode.broadcast_date.slice(0, 10) >= week.start &&
    episode.broadcast_date.slice(0, 10) < week.end,
  );
  const signature = episodes.map((e) => e.id).join("|");
  useEffect(() => {
    setTurn(0);
  }, [signature]);
  useEffect(() => {
    const preference = matchMedia("(prefers-reduced-motion: reduce)");
    const change = () => setDeviceReduced(preference.matches);
    preference.addEventListener("change", change);
    return () => preference.removeEventListener("change", change);
  }, []);
  useEffect(() => {
    const update = () => {
      setForeground(!document.hidden);
      setToday(new Date());
    };
    document.addEventListener("visibilitychange", update);
    const timer = setInterval(() => setToday(new Date()), 60000);
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    if (root.current) observer.observe(root.current);
    return () => {
      document.removeEventListener("visibilitychange", update);
      clearInterval(timer);
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    if (
      !automatic ||
      reduced ||
      hover ||
      !visible ||
      !foreground ||
      player.playing ||
      count < 2
    )
      return;
    const timer = setInterval(() => setTurn((value) => value + 1), 7000);
    return () => clearInterval(timer);
  }, [automatic, reduced, hover, visible, foreground, player.playing, count]);
  function move(delta: number) {
    setAutomatic(false);
    setTurn((value) => value + delta);
  }
  function activate(index: number, delta: number) {
    setAutomatic(false);
    if (delta !== 0) {
      move(delta);
      return;
    }
    const chosen = episodes[index];
    if (!chosen?.recording) return;
    if (player.episode?.id === chosen.id) player.toggle();
    else player.load(chosen);
  }
  return (
    <section
      ref={root}
      className={
        "home-dial " + (appearance.textSize >= 24 ? "large-reading" : "")
      }
      aria-labelledby="weekly-heading"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocusCapture={(e) => {
        if (!(e.target as HTMLElement).closest(".rotation-toggle"))
          setAutomatic(false);
      }}
    >
      <h1 className="sr-only" id="weekly-heading">
        {t("wheelShows")}
      </h1>
      <div className="dial-meta">
        <p>{t("eyebrow")}</p>
        <p>
          <strong>
            {t(inWeek || !episode ? "thisWeek" : "fromCollection")}
          </strong>
          <span>
            {inWeek || !episode
              ? `${date(week.start, lang)} — ${date(week.last, lang)}`
              : date(episode.broadcast_date, lang)}
          </span>
        </p>
      </div>
      <div
        className="half-carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label={t("wheelShows")}
        onKeyDown={(e) => {
          if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
            e.preventDefault();
            const target = e.target as HTMLElement;
            focusSelected.current = target.closest(".rail-choice")
              ? "rail"
              : target.closest(".dial-choice")
                ? "dial"
                : null;
            const delta =
              e.key === "Home"
                ? -selected
                : e.key === "End"
                  ? count - 1 - selected
                  : (e.key === "ArrowRight" ? 1 : -1) *
                    (lang === "ar" ? -1 : 1);
            move(delta);
          }
        }}
      >
        <div className="dial-presentation">
          <div
            className="dial-window"
            onDragStart={(e) => e.preventDefault()}
            onPointerDown={(e) => {
              pointer.current = { x: e.clientX, y: e.clientY };
              swiped.current = false;
            }}
            onPointerCancel={() => {
              pointer.current = null;
            }}
            onPointerUp={(e) => {
              const start = pointer.current;
              pointer.current = null;
              if (!start || count < 2) return;
              const dx = e.clientX - start.x,
                dy = e.clientY - start.y;
              if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
                swiped.current = true;
                move(
                  (dx < 0 ? 1 : -1) *
                    Math.max(1, Math.round(Math.abs(dx) / 100)) *
                    (lang === "ar" ? -1 : 1),
                );
              }
            }}
            onClickCapture={(e) => {
              if (swiped.current) {
                e.preventDefault();
                e.stopPropagation();
                swiped.current = false;
              }
            }}
          >
            <div
              className="dial-canvas"
              style={
                {
                  "--dial-turn": `${-turn * step}deg`,
                  "--dial-counter": `${turn * step}deg`,
                } as CSSProperties
              }
            >
              <div className="dial-arc arc-outer" aria-hidden="true" />
              <div className="dial-arc arc-inner" aria-hidden="true" />
              <div className="dial-rotor">
                {wheelSlots(turn, count).map(
                  ({ slot, index, offset, visible }) => {
                    const ep = episodes[index];
                    const active = offset === 0;
                    const playing =
                      player.episode?.id === ep.id && player.playing;
                    return (
                      <div
                        key={slot}
                        className={
                          "dial-node " + (offset === 0 ? "is-selected" : "")
                        }
                        aria-hidden={!visible || undefined}
                        style={
                          {
                            "--node-angle": `${slot * step}deg`,
                            "--card-scale": offset === 0 ? 1 : 0.78,
                            opacity: visible ? 1 : 0,
                            pointerEvents: visible ? "auto" : "none",
                          } as CSSProperties
                        }
                      >
                        <button
                          className="dial-choice"
                          tabIndex={offset === 0 ? 0 : -1}
                          aria-current={offset === 0 ? "true" : undefined}
                          aria-label={`${active ? t(playing ? "pause" : "play") : t("selectShow") + " " + (index + 1)}: ${ep.show_title || title(ep)} · ${ep.station_name}`}
                          disabled={active && !ep.recording}
                          aria-controls={
                            active ? undefined : "weekly-selection"
                          }
                          onClick={() => activate(index, offset)}
                        >
                          <span className="dial-frame">
                            <Artwork episode={ep} />
                            {active && ep.recording && (
                              <span
                                className="dial-play-overlay"
                                aria-hidden="true"
                              >
                                <WheelPlaybackGlyph playing={playing} />
                              </span>
                            )}
                          </span>
                          {!active && visible && (
                            <span className="dial-neighbour" aria-hidden="true">
                              <strong dir="auto">
                                {ep.show_title || title(ep)}
                              </strong>
                              <span>{ep.station_name}</span>
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
          <div
            className="dial-selection"
            id="weekly-selection"
            aria-live={automatic && !reduced ? "off" : "polite"}
            aria-atomic="true"
          >
            {episode ? (
              <div className="dial-selection-copy" key={episode.id}>
                <p className="dial-station">
                  {episode.station_name}{" "}
                  <span>/ {country(episode.country, lang)}</span>
                </p>
                <h2>
                  <a href={route("episode", episode.id)} dir="auto">
                    {episode.show_title || title(episode)}
                  </a>
                </h2>
                {title(episode) !== episode.show_title && (
                  <p className="dial-episode-title" dir="auto">
                    {title(episode)}
                  </p>
                )}
                <p className="dial-episode-meta">
                  <span>{date(episode.broadcast_date, lang)}</span>
                  {episode.recording && (
                    <span>
                      <Icon name="headphones" size={18} />{" "}
                      {clock(episode.recording.duration_ms)}
                    </span>
                  )}
                  {episode.languages.length > 0 && (
                    <span>
                      <Icon name="globe" size={18} />{" "}
                      {episode.languages
                        .map((code) => language(code, lang))
                        .join(" / ")}
                    </span>
                  )}
                </p>
                <a
                  className="dial-detail-link"
                  href={route("episode", episode.id)}
                >
                  {t("details")} <Icon name="arrow" size={18} />
                </a>
              </div>
            ) : (
              <p>{t("emptyWeek")}</p>
            )}
            <div className="dial-bottom">
              <span className="dial-position" aria-live="off">
                {count ? `${selected + 1} / ${count}` : "—"}
              </span>
              <button
                className="rotation-toggle"
                disabled={count < 2 || reduced}
                onClick={() => setAutomatic((value) => !value)}
              >
                <Icon name={automatic && !reduced ? "pause" : "play"} />
                {t(
                  reduced
                    ? "reducedMotion"
                    : automatic
                      ? "pauseRotation"
                      : "startRotation",
                )}
              </button>
            </div>
          </div>
          <div className="dial-controls">
            <button
              className="icon-button"
              aria-label={t("previousShow")}
              disabled={count < 2}
              onClick={() => move(-1)}
            >
              <Icon name="back" />
            </button>
            <button
              className="icon-button"
              aria-label={t("nextShow")}
              disabled={count < 2}
              onClick={() => move(1)}
            >
              <Icon name="arrow" />
            </button>
          </div>
        </div>
        {count > 0 && (
          <div className="dial-index">
            <div className="dial-index-head">
              <h2>
                {t("allShows")} <span>{count}</span>
              </h2>
              <p>{t("circlePlayHint")}</p>
            </div>
            <div
              ref={rail}
              className="dial-rail"
              role="group"
              aria-label={t("allShows")}
            >
              {episodes.map((ep, index) => (
                <button
                  key={ep.id}
                  className="rail-choice"
                  aria-pressed={index === selected}
                  tabIndex={index === selected ? 0 : -1}
                  aria-label={`${t("selectShow")} ${index + 1}: ${ep.show_title || title(ep)} · ${ep.station_name}`}
                  aria-controls="weekly-selection"
                  onClick={() => move(wheelDelta(index, selected, count))}
                >
                  <span className="rail-cover">
                    <Artwork episode={ep} />
                  </span>
                  <span className="rail-title" dir="auto">
                    {ep.show_title || title(ep)}
                  </span>
                  <span className="rail-station">{ep.station_name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
